import React from "react";
import { DeleteConfirmationModal } from "../../../designSystem/compositeComponents/DeleteConfirmationButton";
import { useHistory } from "react-router-dom";
import { deleteActivity, getMeetingInvitationStatus, sendMeetingInvitation } from "../../../api/activityClientApi";
import { Activity, ActivityType, asyncSetTimeout, MeetingInvitationStatus } from "../../../amorg-common";
import { txt } from "../../../txt";
import { FlexItem } from "../../../designSystem/compositeComponents/FlexHelpers";
import PrintMeetingActivity from "../common/PrintMeetingActivity";
import { P } from "../../../designSystem/typography/Body";
import { MessageCard } from "../../../designSystem/Message";
import { ToolBarButton, ToolBarButtonProps, ToolBarContainer } from "../../../designSystem/compositeComponents/ButtonToolBar";
import { Icon } from "../../../designSystem/icons/Icons";
import styled from "styled-components";
import { colors } from "../../../designSystem/colors/color";
import { AnimatedCollapsibleContent } from "../../../designSystem/collapsible/AnimatedCollapsibleContent";
import { useReactToPrint } from "react-to-print";
import { PageHeader } from "../../../designSystem/PageHeader";

type ActivityHeaderProps =  {
    activity: Activity;
    deleteConfirmationTextInModal?: string;
    onActivityUpdated?: () => void;
    handleClickSendInvitation?: () => void;
    isLoading?: (value:boolean) => void;
    additionalToolBarButtons?:ToolBarButtonProps[];
    breadCrumbs?:{text:string, url:string};
}

export function ActivityHeader(props:ActivityHeaderProps){

    const [inDelete, setInDelete] = React.useState(false);
    const [invitationStatus, setInvitationStatus] = React.useState<MeetingInvitationStatus>(null);
    

    const history = useHistory();

    React.useEffect(() => {
        async function getInvitationStatus() {
            setInvitationStatus(await getMeetingInvitationStatus(props.activity.id));
            props.isLoading?.(false);
        }
        if(showInvitationButtons)
        {
            getInvitationStatus();
        }
    }, [props.activity]);

    
    async function handleClickSendInvitation()
    {
        if(showInvitationButtons)
        {
            props.isLoading?.(true);
            await asyncSetTimeout(async () => { return; }, 1000);
            sendMeetingInvitation(props.activity.id);
            props.onActivityUpdated();
            props.isLoading?.(false);
        }
    }

    async function handleClickDeleteActivity() 
    {
        await deleteActivity(props.activity.id);
        setInDelete(false);
        history.goBack();
    }

    function downloadIcsFile() {
        if(showInvitationButtons)
        {
            fetch(`/api/activities/${props.activity.id}/ics`)
                .then(response => response.blob())
                .then(blob => {
                    const url = window.URL.createObjectURL(new Blob([blob]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `${props.activity.id}.ics`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                });
        }
    }
    
    const invitationActivityTypes:ActivityType[] = [
        "WorkgroupMeeting",
        "AnnualWorkgroupStatus",
    ];
    const showInvitationButtons = invitationActivityTypes.includes(props.activity.activityType)
    const showInvitationWarning:boolean = 
        showInvitationButtons &&
        (invitationStatus && props.activity.start > new Date())
        && 
        (invitationStatus.status === 'not-sent' || invitationStatus.status === 'has-changes') 
        &&
        (props.activity.participants.length > 0);


    const printActivityTypes:ActivityType[] = [
        "WorkgroupMeeting",
        "AnnualWorkgroupStatus",
        "SafetyInspection",
    ];
    const showPrintButton = printActivityTypes.includes(props.activity.activityType);

    const printRef = React.useRef(null);
    const printFn = useReactToPrint({
        contentRef: printRef,
        documentTitle: txt.getDynamic(`ActivityType_${props.activity.activityType}`)
    });
    
    return (
        <>
        
        <PageHeader
            headerText={txt.getDynamic(`ActivityType_${props.activity.activityType}`)}
            headerStyle="secondary-gray"
            buttons={
                <ToolBarContainer>
                    {props.additionalToolBarButtons && props.additionalToolBarButtons.map((button, index) => {
                        return <ToolBarButton key={index} {...button} />
                    })}
                    {showInvitationButtons &&
                        <ToolBarButton
                            icon="interface_actions_send"
                            tooltip="Send invitation"
                            onClick={ () => handleClickSendInvitation()}
                            variant="text" />
                    }
                    {showInvitationButtons &&
                        <ToolBarButton
                            icon='navigation_newCalendar'
                            tooltip= 'Tilføj til kalender'
                            onClick={ () => downloadIcsFile()}
                            variant= 'text' />
                    }
                    {showPrintButton &&
                        <>
                            <ToolBarButton
                                icon='interface_actions_print'
                                tooltip= 'Print'
                                variant= 'text'
                                onClick={printFn}
                            />

                            <PrintMeetingActivity 
                                activity={props.activity}
                                innerRef={printRef} />
                        </> 
                    }
                    
                    <ToolBarButton
                        icon='interface_actions_delete'
                        tooltip= 'Slet'
                        onClick={ () => setInDelete(true)}
                        variant= 'destructive-text' />

                    <DeleteConfirmationModal
                        showModal={inDelete}
                        buttonText={"Slet " + txt.getDynamic(`ActivityType_${props.activity.activityType}`)}
                        confirmationHeading={"Slet " + txt.getDynamic(`ActivityType_${props.activity.activityType}`) + "?"}
                        confirmationText={props.deleteConfirmationTextInModal ? props.deleteConfirmationTextInModal : ""}
                        requireKey={true}
                        onConfirmDelete={handleClickDeleteActivity} 
                        onCancel={() => setInDelete(false)} />
                </ToolBarContainer>
            } />
        
            {showInvitationWarning && 
                <AnimatedCollapsibleContent open={true}>
                    <ActivityUpdatedMessage status={invitationStatus.status} onClick={handleClickSendInvitation} />
                </AnimatedCollapsibleContent>
            }
        </>
    );
}

type ActivityUpdatedMessageProps = {
    status: string;
    onClick: () => void;
}

function ActivityUpdatedMessage(props: ActivityUpdatedMessageProps) {
    return <>
        <MessageContainer>
            <FlexItem flexGrow="1">
                {props.status === 'has-changes' && 
                <MessageCard variant="info" textAlign="left" className={props.status === 'has-changes' ? 'active' : 'notActive' }>
                    <P><b>Mødet er blevet opdateret</b></P>
                    <P>Opdater nye, eksisterende og fjernede deltagere om ændringerne med en e-mail.</P> 
                </MessageCard>
                }
                {props.status === 'not-sent' &&
                <MessageCard variant="info" textAlign="left" className={props.status === 'not-sent' ? 'active' : 'notActive' }>
                    <P><b>Mødet er oprettet</b></P>
                    <P>Send en e-mail invitation til deltagerne.</P> 
                </MessageCard>
                }
            </FlexItem>
            <MessageButton onClick={props.onClick} />
        </MessageContainer>
    </>;
}

type MessageButtonProps = {
    onClick: () => void;
}

function MessageButton(props: MessageButtonProps) {
    return (
        <MessageButtonStyled onClick={props.onClick}>
            <Icon name='interface_actions_send' size="medium" color={colors.icon.active} />
        </MessageButtonStyled>
    );
}

const MessageButtonStyled = styled.div`
    border:1px solid ${colors.messageCard.info.border};
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    cursor: pointer;
    align-self: stretch;
    align-content: center;
    padding: 0 8px;
    transition: background 0.2s ease-in;

    &:hover {
        background: ${colors.messageCard.backgroundHover};
    }
    
`

const MessageContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 1px;
    align-items: center;
    
    overflow: hidden;
    
    & .active {
        padding: 12px 16px;
    }
    

`;